import { mobileApiClient } from '@/utils/api'
import nativeBridge from './nativeBridge'

class StorageService {
  constructor() {
    this.config = null
    this.isInitialized = false
  }

  /**
   * Initialize storage service by fetching configuration from backend
   * @returns {Promise<void>}
   */
  async initialize() {
    if (this.isInitialized) return

    try {
      const response = await mobileApiClient.get('/storage/config')
      if (response.DidError === false && response.Model) {
        this.config = response.Model
        this.isInitialized = true
        console.log('Storage service initialized with method:', this.config.Method)
      } else {
        console.warn('Failed to get storage config from backend')
        this.config = { Method: 'local' }
      }
    } catch (error) {
      console.warn('Error fetching storage config, using local:', error)
      this.config = { Method: 'local' }
    }
  }

  /**
   * Get current storage method
   * @returns {string} Storage method
   */
  getCurrentMethod() {
    return this.config?.Method || 'local'
  }

  /**
   * Check if Firebase is enabled
   * @returns {boolean}
   */
  isFirebaseEnabled() {
    return this.getCurrentMethod() === 'firebase'
  }

  /**
   * Check if Local storage is enabled
   * @returns {boolean}
   */
  isLocalStorageEnabled() {
    return this.getCurrentMethod() === 'local'
  }

  /**
   * Get storage configuration
   * @returns {Object} Storage configuration
   */
  getConfig() {
    return this.config || { Method: 'local' }
  }

  /**
   * Upload file using backend API with proper model structure
   * @param {File} file - File to upload
   * @param {Object} options - Upload options
   * @returns {Promise<Object>} Upload result
   */
  async uploadFile(file, options = {}) {
    // Initialize if not already done
    if (!this.isInitialized) {
      await this.initialize()
    }

    const {
      documentType = 20, // Default to Others
      userId,
      documentName,
      description = '',
      referenceNo = '',
      note = ''
    } = options

    // Validate file
    const validation = this.validateFile(file)
    if (!validation.valid) {
      throw new Error(validation.error)
    }

    // Create FormData for API upload with proper field names
    const formData = new FormData()
    formData.append('Files', file) // Backend expects 'Files' field
    formData.append('UserId', userId)
    formData.append('Doctitle', documentName || file.name)
    formData.append('Docname', file.name)
    formData.append('Doctype', documentType)
    formData.append('Referenceno', referenceNo)
    formData.append('Noteno', note || description)

    try {
      // Send to backend API - backend handles Firebase/Local logic
      const response = await mobileApiClient.post('/Documents/UploadDocumentFile', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

      return {
        success: !response.DidError,
        ...response,
        storageMethod: this.getCurrentMethod()
      }
    } catch (error) {
      console.error('Upload error:', error)
      throw new Error(`Failed to upload file: ${error.message}`)
    }
  }

  /**
   * Get file download URL or trigger download
   * @param {Object} documentInfo - Document information from backend
   * @returns {Promise<string>} Download URL or null if direct download needed
   */
  async getDownloadURL(documentInfo) {
    try {
      // If FileUrl is available, use it directly
      if (documentInfo.FileUrl) {
        return documentInfo.FileUrl
      }

      // If no FileUrl, we need to call the download endpoint
      // This will return a blob or redirect to the file
      const response = await mobileApiClient.get(`/Documents/DownloadDocumentFile/${documentInfo.Docid}`, {
        responseType: 'blob'
      })
      
      // Create a blob URL for the downloaded file
      const blob = new Blob([response])
      return URL.createObjectURL(blob)
    } catch (error) {
      console.error('Error getting download URL:', error)
      throw new Error(`Failed to get download URL: ${error.message}`)
    }
  }

  /**
   * Download file with iOS-specific handling
   * @param {Object} documentInfo - Document information
   * @returns {Promise<void>}
   */
  async downloadFile(documentInfo) {
    try {
      console.log('Downloading file:', documentInfo.Docname)
      
      // Check if running in Flutter app and on iOS
      if (nativeBridge.isRunningInFlutter() && nativeBridge.isIOS()) {
        console.log('Using native iOS document download')
        const result = await nativeBridge.downloadDocument(documentInfo)
        if (result) {
          console.log('Document downloaded via native iOS method')
          return
        }
      }

      // Fallback to web-based download - trigger OS download instead of new tab
      let downloadUrl

      if (documentInfo.FileUrl) {
        // Use direct FileUrl if available
        downloadUrl = documentInfo.FileUrl
      } else {
        // Call download endpoint
        const response = await mobileApiClient.get(`/Documents/DownloadDocumentFile/${documentInfo.Docid}`, {
          responseType: 'blob'
        })
        
        // Create blob URL
        const blob = new Blob([response])
        downloadUrl = URL.createObjectURL(blob)
      }

      // Create download link and trigger OS download
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = documentInfo.Docname || 'document'
      link.style.display = 'none' // Hide the link
      link.target = '_self' // Prevent opening in new tab
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      // Clean up blob URL if we created one
      if (!documentInfo.FileUrl) {
        setTimeout(() => URL.revokeObjectURL(downloadUrl), 1000)
      }
      
      console.log('Download triggered successfully')
    } catch (error) {
      console.error('Download error:', error)
      throw new Error(`Failed to download file: ${error.message}`)
    }
  }

  /**
   * View file (for images) or download (for other files) with iOS-specific handling
   * @param {Object} documentInfo - Document information
   * @returns {Promise<void>}
   */
  async viewFile(documentInfo) {
    try {
      // Check if running in Flutter app and on iOS
      if (nativeBridge.isRunningInFlutter() && nativeBridge.isIOS()) {
        console.log('Using native iOS document viewer')
        const result = await nativeBridge.viewDocument(documentInfo)
        if (result) {
          console.log('Document viewed via native iOS method')
          return
        }
      }

      // Fallback to web-based viewing - trigger OS download instead of new tab
      const isImage = this.isImageFile(documentInfo.Docextension)
      
      // For all files, trigger OS download instead of opening in new tab
      await this.downloadFile(documentInfo)
    } catch (error) {
      console.error('View error:', error)
      throw new Error(`Failed to view file: ${error.message}`)
    }
  }

  /**
   * Check if file is an image based on extension
   * @param {string} extension - File extension
   * @returns {boolean}
   */
  isImageFile(extension) {
    if (!extension) return false
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'tiff', 'tif', 'ico']
    const ext = extension.toLowerCase().replace('.', '')
    return imageExtensions.includes(ext)
  }

  /**
   * Delete file via backend API
   * @param {Object} fileInfo - File information
   * @returns {Promise<boolean>} Success status
   */
  async deleteFile(fileInfo) {
    try {
      await mobileApiClient.delete(`/Documents/delete_document/${fileInfo.id}`)
      return true
    } catch (error) {
      console.error('Delete error:', error)
      throw new Error(`Failed to delete file: ${error.message}`)
    }
  }

  /**
   * Validate file
   * @param {File} file - File to validate
   * @returns {Object} Validation result
   */
  validateFile(file) {
    const maxSize = 10 * 1024 * 1024 // 10MB default
    const allowedTypes = [
      // Document formats
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'application/rtf',
      'text/plain',
      'text/csv',
      'application/xml',
      'application/json',
      'application/zip',
      'application/x-rar-compressed',
      'application/x-7z-compressed',
      'application/x-tar',
      'application/x-gzip',
      
      // Image formats
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'image/bmp',
      'image/webp',
      'image/svg+xml',
      'image/tiff',
      'image/tif',
      'image/x-icon',
      'image/vnd.microsoft.icon',
      
      // Additional document formats
      'application/vnd.oasis.opendocument.text',
      'application/vnd.oasis.opendocument.spreadsheet',
      'application/vnd.oasis.opendocument.presentation',
      'application/vnd.apple.pages',
      'application/vnd.apple.numbers',
      'application/vnd.apple.keynote',
      'application/vnd.google-apps.document',
      'application/vnd.google-apps.spreadsheet',
      'application/vnd.google-apps.presentation'
    ]

    // File extensions for fallback validation
    const allowedExtensions = [
      // Documents
      'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'rtf', 'txt', 'csv',
      'xml', 'json', 'zip', 'rar', '7z', 'tar', 'gz', 'odt', 'ods', 'odp',
      'pages', 'numbers', 'key',
      
      // Images
      'jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'tiff', 'tif', 'ico'
    ]

    if (file.size > maxSize) {
      return {
        valid: false,
        error: `File size exceeds maximum limit of ${maxSize / (1024 * 1024)}MB`
      }
    }

    // Check both MIME type and file extension
    const fileExtension = file.name.split('.').pop()?.toLowerCase()
    const isAllowedByType = allowedTypes.includes(file.type)
    const isAllowedByExtension = allowedExtensions.includes(fileExtension)
    
    if (!isAllowedByType && !isAllowedByExtension) {
      return {
        valid: false,
        error: `File type not allowed. Allowed: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, RTF, TXT, CSV, ZIP, RAR, JPG, PNG, GIF, BMP, WEBP, SVG, TIFF. Detected: ${file.type || 'unknown'} (${fileExtension})`
      }
    }

    return { valid: true }
  }

  /**
   * Get storage method info
   * @returns {Object} Storage method information
   */
  getStorageInfo() {
    return {
      method: this.getCurrentMethod(),
      isFirebase: this.isFirebaseEnabled(),
      isLocal: this.isLocalStorageEnabled(),
      config: this.getConfig(),
      isInitialized: this.isInitialized
    }
  }

  /**
   * Refresh configuration from backend
   * @returns {Promise<void>}
   */
  async refreshConfig() {
    this.isInitialized = false
    await this.initialize()
  }

  /**
   * iOS-specific document handling to prevent navigation issues
   * @param {Object} documentInfo - Document information
   * @returns {Promise<void>}
   */
  async handleIOSDocument(documentInfo, action = 'view') {
    try {
      // Check if running in Flutter app and on iOS
      if (nativeBridge.isRunningInFlutter() && nativeBridge.isIOS()) {
        console.log(`Using native iOS document ${action}`)
        
        let result = false
        switch (action) {
          case 'download':
            result = await nativeBridge.downloadDocument(documentInfo)
            break
          case 'view':
            result = await nativeBridge.viewDocument(documentInfo)
            break
          case 'open':
            result = await nativeBridge.openDocumentInApp(documentInfo)
            break
          default:
            result = await nativeBridge.viewDocument(documentInfo)
        }
        
        if (result) {
          console.log(`Document ${action} via native iOS method`)
          return
        }
      }

      // Fallback to web-based handling
      console.log(`Using web-based document ${action}`)
      if (action === 'download') {
        await this.downloadFile(documentInfo)
      } else {
        await this.viewFile(documentInfo)
      }
    } catch (error) {
      console.error(`iOS document ${action} error:`, error)
      throw new Error(`Failed to ${action} document: ${error.message}`)
    }
  }
}

export default new StorageService() 